import React, { useMemo, useState } from 'react';
import { LuCheck, LuLoader, LuPencil, LuPlus, LuTags, LuTrash2, LuUser, LuX } from 'react-icons/lu';

import type { GUID, PersonOrg } from '@ndvr/auth-api';
import type { Keyword } from '@ndvr/customer-api';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Combobox } from '@/components/ui/combobox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { getPersonName } from 'utils';
import { useAuthContext } from 'views/auth/Auth';

import { usePersonProfilesForTenant } from '../../person/person-queries';
import { WorkspaceSettingBanner } from '../WorkspaceSettingBanner';
import {
    useAllKeywordAssociations,
    useAssociateKeyword,
    useCreateKeyword,
    useDeleteKeyword,
    useDisassociateKeyword,
    useKeywords,
    usePersonOrgsForKeyword,
    useUpdateKeyword,
} from './keyword-queries';
import { KeywordBulkEditor } from './KeywordBulkEditor';

export const KeywordSegmentation = () => {
    const { availableTenants, currentTenant, setTenant } = useAuthContext();
    const { data: keywords = [], isPending: keywordsLoading } = useKeywords();
    const { data: personProfiles = [], isPending: personProfilesLoading } = usePersonProfilesForTenant();
    const createKeywordMutation = useCreateKeyword();
    const updateKeywordMutation = useUpdateKeyword();
    const deleteKeywordMutation = useDeleteKeyword();
    const associateKeywordMutation = useAssociateKeyword();
    const disassociateKeywordMutation = useDisassociateKeyword();

    const isMutatingKeyword = [createKeywordMutation, updateKeywordMutation, deleteKeywordMutation].some(
        ({ isPending }) => isPending,
    );

    const [isAddingKeyword, setIsAddingKeyword] = useState(false);
    const [newKeyword, setNewKeyword] = useState('');
    const [editingKeywordId, setEditingKeywordId] = useState<GUID<Keyword> | null>(null);
    const [editingKeywordValue, setEditingKeywordValue] = useState('');
    const [keywordToDelete, setKeywordToDelete] = useState<Keyword | null>(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
    const [selectedKeyword, setSelectedKeyword] = useState<Keyword | null>(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [isAssigningPerson, setIsAssigningPerson] = useState(false);
    const [loadingAssociations, setLoadingAssociations] = useState<Set<string>>(new Set());

    const handleTenantChange = (tenantGUID: string) => {
        setIsAddingKeyword(false);
        setNewKeyword('');
        setEditingKeywordId(null);
        setEditingKeywordValue('');
        setKeywordToDelete(null);
        setDeleteConfirmOpen(false);
        setSelectedKeyword(null);
        setDrawerOpen(false);
        setIsAssigningPerson(false);
        setTenant(tenantGUID);
    };

    const { data: personOrgGUIDs, isLoading: personOrgGUIDsLoading } = usePersonOrgsForKeyword(
        selectedKeyword?.keywordGUID,
    );

    // Fetch all keyword associations for the bulk editor
    const { data: allKeywordAssociations, isLoading: associationsLoading } = useAllKeywordAssociations(keywords);

    // Build a mapping of personOrgGUID -> keyword labels
    const personKeywordMap = useMemo(() => {
        const map = new Map<string, string[]>();
        if (Array.isArray(allKeywordAssociations)) {
            allKeywordAssociations.forEach(({ keywordLabel, personOrgs }) => {
                if (Array.isArray(personOrgs)) {
                    personOrgs.forEach((personOrgGUID) => {
                        const existing = map.get(personOrgGUID) || [];
                        map.set(personOrgGUID, [...existing, keywordLabel]);
                    });
                }
            });
        }
        return map;
    }, [allKeywordAssociations]);

    // Transform data for bulk editor
    const personsForBulkEditor = useMemo(() => {
        if (!Array.isArray(personProfiles)) return [];
        return personProfiles.map((profile) => {
            const shortProfile = profile.personOrgShortProfile;
            const personOrgGUID = shortProfile.personOrgGUID;
            const personKeywords = personKeywordMap.get(personOrgGUID) || [];
            return {
                id: personOrgGUID,
                name: getPersonName(shortProfile),
                email: shortProfile.primaryEmail?.plainText || '',
                household: '',
                keywords: personKeywords,
            };
        });
    }, [personProfiles, personKeywordMap]);

    const keywordsForBulkEditor = useMemo(() => {
        if (!Array.isArray(keywords)) return [];
        return keywords.map((keyword) => ({
            id: keyword.keywordGUID,
            keyword: keyword.label,
        }));
    }, [keywords]);

    const handleToggleAssociation = async (personOrgGUID: GUID<PersonOrg>, keywordLabel: string) => {
        const keyword = keywords.find((k) => k.label === keywordLabel);
        if (!keyword) return;

        const loadingKey = `${personOrgGUID}-${keywordLabel}`;
        setLoadingAssociations((prev) => new Set(prev).add(loadingKey));

        try {
            const isCurrentlyAssociated = personKeywordMap.get(personOrgGUID)?.includes(keywordLabel);

            if (isCurrentlyAssociated) {
                await disassociateKeywordMutation.mutateAsync({
                    personOrgGUID,
                    keywordGUID: keyword.keywordGUID,
                });
            } else {
                await associateKeywordMutation.mutateAsync({
                    personOrgGUID,
                    keywordGUID: keyword.keywordGUID,
                });
            }
        } finally {
            setLoadingAssociations((prev) => {
                const next = new Set(prev);
                next.delete(loadingKey);
                return next;
            });
        }
    };

    const personsForKeyword = Array.isArray(personOrgGUIDs)
        ? personOrgGUIDs
              .map((guid) => {
                  const profile = personProfiles.find((p) => p.personOrgShortProfile.personOrgGUID === guid);
                  if (!profile) return null;
                  const shortProfile = profile.personOrgShortProfile;
                  return {
                      id: guid,
                      name: getPersonName(shortProfile),
                      email: shortProfile.primaryEmail?.plainText || '',
                  };
              })
              .filter((p): p is NonNullable<typeof p> => p !== null)
        : [];

    const handleAddKeyword = async () => {
        if (newKeyword.trim()) {
            await createKeywordMutation.mutateAsync({ label: newKeyword.trim() });
            setNewKeyword('');
            setIsAddingKeyword(false);
        }
    };

    const handleStartEditKeyword = (e: React.MouseEvent, keyword: Keyword) => {
        e.stopPropagation();
        setEditingKeywordId(keyword.keywordGUID);
        setEditingKeywordValue(keyword.label);
    };

    const handleSaveKeyword = async (keywordGUID: GUID<Keyword>) => {
        if (editingKeywordValue.trim()) {
            await updateKeywordMutation.mutateAsync({
                keywordGUID,
                request: { label: editingKeywordValue.trim() },
            });
            setEditingKeywordId(null);
            setEditingKeywordValue('');
        }
    };

    const handleCancelEditKeyword = () => {
        setEditingKeywordId(null);
        setEditingKeywordValue('');
    };

    const handleDeleteKeyword = async () => {
        if (keywordToDelete) {
            await deleteKeywordMutation.mutateAsync(keywordToDelete.keywordGUID);
            setKeywordToDelete(null);
            setDeleteConfirmOpen(false);
        }
    };

    const handleKeywordClick = (keyword: Keyword) => {
        if (editingKeywordId !== keyword.keywordGUID) {
            setSelectedKeyword(keyword);
            setDrawerOpen(true);
        }
    };

    const getInitials = (name: string) => {
        return name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    const availablePersonOptions =
        Array.isArray(personProfiles) && Array.isArray(personOrgGUIDs)
            ? personProfiles
                  .filter((profile) => !personOrgGUIDs.includes(profile.personOrgShortProfile.personOrgGUID))
                  .map((profile) => ({
                      value: profile.personOrgShortProfile.personOrgGUID,
                      label: getPersonName(profile.personOrgShortProfile),
                  }))
            : [];

    const handleAssignPerson = async (personOrgGUID: GUID<PersonOrg>) => {
        if (selectedKeyword && personOrgGUID) {
            await associateKeywordMutation.mutateAsync({
                personOrgGUID,
                keywordGUID: selectedKeyword.keywordGUID,
            });
            setIsAssigningPerson(false);
        }
    };

    const handleDisassociatePerson = async (personOrgGUID: GUID<PersonOrg>) => {
        if (selectedKeyword) {
            await disassociateKeywordMutation.mutateAsync({
                personOrgGUID,
                keywordGUID: selectedKeyword.keywordGUID,
            });
        }
    };

    if (keywordsLoading) {
        return (
            <div className="space-y-6">
                <WorkspaceSettingBanner
                    resourceLabel="Segmentation keywords"
                    availableTenants={availableTenants}
                    currentTenant={currentTenant}
                    disabled={isMutatingKeyword}
                    onTenantChange={handleTenantChange}
                />
                <Card>
                    <CardContent className="py-8">
                        <div className="text-muted-foreground flex items-center justify-center">
                            Loading keywords...
                        </div>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <WorkspaceSettingBanner
                resourceLabel="Segmentation keywords"
                availableTenants={availableTenants}
                currentTenant={currentTenant}
                disabled={isMutatingKeyword}
                onTenantChange={handleTenantChange}
            />
            <Card>
                <CardHeader className="py-4">
                    <div className="flex items-center justify-between">
                        <CardTitle className="flex items-center gap-2 text-base font-medium">
                            <LuTags className="h-4 w-4" />
                            Segmentation Keywords
                        </CardTitle>
                        {!isAddingKeyword && (
                            <Button size="sm" onClick={() => setIsAddingKeyword(true)}>
                                <LuPlus className="mr-1 h-4 w-4" />
                                Add Keyword
                            </Button>
                        )}
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="flex flex-wrap gap-2">
                        {Array.isArray(keywords) &&
                            keywords.map((keyword) => (
                                <div
                                    key={keyword.keywordGUID}
                                    onClick={() => handleKeywordClick(keyword)}
                                    className="bg-muted hover:bg-accent hover:border-accent inline-flex cursor-pointer items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm transition-colors"
                                >
                                    {editingKeywordId === keyword.keywordGUID ? (
                                        <>
                                            <Input
                                                value={editingKeywordValue}
                                                onChange={(e) => setEditingKeywordValue(e.target.value)}
                                                className="h-6 w-32 px-2 text-sm"
                                                autoFocus
                                                onClick={(e) => e.stopPropagation()}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') handleSaveKeyword(keyword.keywordGUID);
                                                    if (e.key === 'Escape') handleCancelEditKeyword();
                                                }}
                                            />
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleSaveKeyword(keyword.keywordGUID);
                                                }}
                                                className="rounded p-0.5 text-green-600 hover:bg-green-50"
                                            >
                                                <LuCheck className="h-3.5 w-3.5" />
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleCancelEditKeyword();
                                                }}
                                                className="rounded p-0.5 text-red-600 hover:bg-red-50"
                                            >
                                                <LuX className="h-3.5 w-3.5" />
                                            </button>
                                        </>
                                    ) : (
                                        <>
                                            <span>{keyword.label}</span>
                                            <button
                                                onClick={(e) => handleStartEditKeyword(e, keyword)}
                                                className="text-muted-foreground hover:bg-background/50 hover:text-foreground rounded p-0.5"
                                            >
                                                <LuPencil className="h-3 w-3" />
                                            </button>
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setKeywordToDelete(keyword);
                                                    setDeleteConfirmOpen(true);
                                                }}
                                                className="text-muted-foreground hover:bg-destructive/10 hover:text-destructive rounded p-0.5"
                                            >
                                                <LuTrash2 className="h-3 w-3" />
                                            </button>
                                        </>
                                    )}
                                </div>
                            ))}
                        {isAddingKeyword && (
                            <div className="bg-background inline-flex items-center gap-1.5 rounded-full border border-dashed px-3 py-1.5 text-sm">
                                <Input
                                    value={newKeyword}
                                    onChange={(e) => setNewKeyword(e.target.value)}
                                    placeholder="New keyword..."
                                    className="h-6 w-32 px-2 text-sm"
                                    autoFocus
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') handleAddKeyword();
                                        if (e.key === 'Escape') {
                                            setIsAddingKeyword(false);
                                            setNewKeyword('');
                                        }
                                    }}
                                />
                                <button
                                    onClick={handleAddKeyword}
                                    className="rounded p-0.5 text-green-600 hover:bg-green-50"
                                >
                                    <LuCheck className="h-3.5 w-3.5" />
                                </button>
                                <button
                                    onClick={() => {
                                        setIsAddingKeyword(false);
                                        setNewKeyword('');
                                    }}
                                    className="rounded p-0.5 text-red-600 hover:bg-red-50"
                                >
                                    <LuX className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        )}
                    </div>
                </CardContent>
            </Card>

            {/* Bulk Editor */}
            <KeywordBulkEditor
                persons={personsForBulkEditor}
                keywords={keywordsForBulkEditor}
                onToggleAssociation={handleToggleAssociation}
                isLoading={personProfilesLoading || associationsLoading}
                loadingAssociations={loadingAssociations}
            />

            {/* Persons Drawer */}
            <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
                <SheetContent className="flex flex-col sm:max-w-md">
                    <SheetHeader>
                        <SheetTitle className="flex items-center gap-2">
                            <LuTags className="h-4 w-4" />
                            {selectedKeyword?.label}
                        </SheetTitle>
                    </SheetHeader>
                    <div className="mt-6 min-h-0 flex-1 overflow-y-auto">
                        {personOrgGUIDsLoading ? (
                            <div className="flex h-64 items-center justify-center">
                                <div className="flex flex-col items-center gap-2">
                                    <LuLoader className="text-primary h-8 w-8 animate-spin" />
                                    <p className="text-muted-foreground text-sm">Loading people...</p>
                                </div>
                            </div>
                        ) : (
                            <>
                                <div className="mb-4 flex items-center justify-between gap-2">
                                    <p className="text-muted-foreground text-sm">
                                        {personsForKeyword.length}{' '}
                                        {personsForKeyword.length === 1 ? 'person' : 'people'} associated with this
                                        keyword
                                    </p>
                                    {!isAssigningPerson && (
                                        <Button size="sm" variant="outline" onClick={() => setIsAssigningPerson(true)}>
                                            <LuPlus className="mr-1 h-3 w-3" />
                                            Assign Person
                                        </Button>
                                    )}
                                </div>
                                {isAssigningPerson && (
                                    <div className="mb-4">
                                        <Combobox
                                            options={availablePersonOptions}
                                            placeholder="Select a person..."
                                            noOptionsLabel="No available people"
                                            onValueChange={(value) => {
                                                handleAssignPerson(value);
                                            }}
                                        />
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            onClick={() => setIsAssigningPerson(false)}
                                            className="mt-2 w-full"
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                )}
                                <div className="space-y-3">
                                    {personsForKeyword.map((person) => (
                                        <div
                                            key={person.id}
                                            className="bg-card hover:bg-accent/50 flex items-center gap-3 rounded-lg border p-3 transition-colors"
                                        >
                                            <div className="bg-primary/10 text-primary flex h-10 w-10 items-center justify-center rounded-full text-sm">
                                                {getInitials(person.name)}
                                            </div>
                                            <div className="min-w-0 flex-1">
                                                <p className="truncate text-sm font-medium">{person.name}</p>
                                                <p className="text-muted-foreground truncate text-xs">{person.email}</p>
                                            </div>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => handleDisassociatePerson(person.id)}
                                                className="text-muted-foreground hover:text-destructive h-8 w-8 shrink-0"
                                            >
                                                <LuTrash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    ))}
                                    {personsForKeyword.length === 0 && (
                                        <div className="text-muted-foreground py-8 text-center">
                                            <LuUser className="mx-auto mb-2 h-8 w-8 opacity-50" />
                                            <p className="text-sm">No people associated with this keyword</p>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </SheetContent>
            </Sheet>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
                <DialogContent className="sm:max-w-sm">
                    <DialogHeader>
                        <DialogTitle>Delete Keyword</DialogTitle>
                    </DialogHeader>
                    <p className="text-muted-foreground py-4 text-sm">
                        Are you sure you want to delete &quot;{keywordToDelete?.label}&quot;? This action cannot be
                        undone.
                    </p>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteConfirmOpen(false)}>
                            Cancel
                        </Button>
                        <Button variant="destructive" onClick={handleDeleteKeyword}>
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};
